import * as React from 'react';
import {
    defaultCountries,
    parseCountry,
    FlagImage,
    type CountryData,
} from 'react-international-phone';
import { ChevronDownIcon, Delete, SearchIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { formatPhoneAsYouType } from '@/lib/phone-validation';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

/**
 * Number readout for the call widget's keypad — country selector on the left,
 * the number in the middle, backspace on the right.
 *
 * Deliberately NOT the shared `components/ui/phone-input`: that is a 36px form
 * control used across contacts, campaigns, polls and public forms, and it
 * reads as a stray input bolted above the widget's glass keypad. Reshaping it
 * for the widget would put all those unrelated surfaces at risk, so this is a
 * separate display-first component. It takes its country data from
 * `react-international-phone` — the same upstream source `PhoneInput` uses —
 * rather than coupling to it.
 */

const HOLD_TO_CLEAR_MS = 500;

const normalizeDialCode = (dialCode: string): string =>
    dialCode.startsWith('+') ? dialCode : `+${dialCode}`;

const cleanPhoneValue = (value: string): string => value.replace(/[^\d+]/g, '');

/** Longest matching dial code wins — '+1' must not shadow '+1684'. */
const findCountryForValue = (value: string): CountryData | undefined =>
    [...defaultCountries]
        .sort((a, b) => parseCountry(b).dialCode.length - parseCountry(a).dialCode.length)
        .find((country) => value.startsWith(normalizeDialCode(parseCountry(country).dialCode)));

interface DialerNumberFieldProps {
    /** Raw E.164 value (e.g. `+14155552671`). */
    readonly value: string;
    readonly onChange: (value: string) => void;
    readonly defaultCountry: string;
}

export function DialerNumberField({ value, onChange, defaultCountry }: DialerNumberFieldProps) {
    const [isOpen, setIsOpen] = React.useState(false);
    const [search, setSearch] = React.useState('');
    const holdTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const didHoldClear = React.useRef(false);

    const selectedCountry = React.useMemo(() => {
        const matched = findCountryForValue(value);
        if (matched) return parseCountry(matched);
        const fallback =
            defaultCountries.find(
                (c) => parseCountry(c).iso2.toLowerCase() === defaultCountry.toLowerCase(),
            ) ?? defaultCountries[0]!;
        return parseCountry(fallback);
    }, [value, defaultCountry]);

    const filteredCountries = React.useMemo(() => {
        if (!search) return defaultCountries;
        const query = search.toLowerCase();
        return defaultCountries.filter((country) => {
            const parsed = parseCountry(country);
            return (
                parsed.name.toLowerCase().includes(query) ||
                parsed.dialCode.includes(query.replace(/\D/g, ''))
            );
        });
    }, [search]);

    const dialCode = normalizeDialCode(selectedCountry.dialCode);
    /** Subscriber digits only — what the user actually typed after the prefix. */
    const subscriber = value.startsWith(dialCode) ? value.slice(dialCode.length) : '';

    const handleCountrySelect = (country: CountryData): void => {
        const parsed = parseCountry(country);
        onChange(`${normalizeDialCode(parsed.dialCode)}${subscriber}`);
        setIsOpen(false);
        setSearch('');
    };

    const handleBackspace = (): void => {
        // A long-press already cleared everything; don't also eat a digit.
        if (didHoldClear.current) return;
        onChange(value.slice(0, -1));
    };

    const startHold = (): void => {
        didHoldClear.current = false;
        holdTimer.current = setTimeout(() => {
            didHoldClear.current = true;
            onChange('');
        }, HOLD_TO_CLEAR_MS);
    };

    const cancelHold = (): void => {
        if (holdTimer.current) clearTimeout(holdTimer.current);
        holdTimer.current = null;
    };

    React.useEffect(() => cancelHold, []);

    return (
        <div
            data-slot="dialer-number-field"
            className="flex items-center gap-1 rounded-2xl border border-white/60 bg-white/55 px-1.5 py-1 backdrop-blur-sm dark:border-white/[0.12] dark:bg-white/[0.06]"
        >
            <Popover open={isOpen} onOpenChange={setIsOpen} modal>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        aria-label={`Country: ${selectedCountry.name}`}
                        className="flex shrink-0 items-center gap-1 rounded-xl px-1.5 py-1.5 transition-colors hover:bg-white/70 focus-visible:ring-2 focus-visible:ring-primary-500/50 focus-visible:outline-none dark:hover:bg-white/[0.12]"
                    >
                        <FlagImage
                            iso2={selectedCountry.iso2}
                            style={{ width: '20px', height: '15px', flexShrink: 0 }}
                        />
                        <ChevronDownIcon
                            className={cn(
                                'size-3 text-text-tertiary transition-transform',
                                isOpen && 'rotate-180',
                            )}
                            aria-hidden="true"
                        />
                    </button>
                </PopoverTrigger>

                <PopoverContent className="w-64 gap-0 p-0" align="start" sideOffset={6}>
                    <div className="border-b border-border-default px-1">
                        <Input
                            startIcon={<SearchIcon />}
                            placeholder="Search country..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="h-8 rounded-none border-0 shadow-none focus-visible:border-0 focus-visible:shadow-none"
                            data-size="sm"
                        />
                    </div>
                    <div className="max-h-56 overflow-y-auto overscroll-contain p-1">
                        {filteredCountries.length === 0 ? (
                            <div className="py-4 text-center text-sm text-text-tertiary">
                                No countries found.
                            </div>
                        ) : (
                            filteredCountries.map((country) => {
                                const parsed = parseCountry(country);
                                return (
                                    <button
                                        key={parsed.iso2}
                                        type="button"
                                        onClick={() => handleCountrySelect(country)}
                                        className={cn(
                                            'flex w-full items-center gap-2.5 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-bg-raised',
                                            selectedCountry.iso2 === parsed.iso2 &&
                                                'bg-primary-50 text-primary-600 dark:bg-primary-900 dark:text-primary-300',
                                        )}
                                    >
                                        <FlagImage
                                            iso2={parsed.iso2}
                                            style={{ width: '20px', height: '15px', flexShrink: 0 }}
                                        />
                                        <span className="min-w-0 flex-1 truncate">
                                            {parsed.name}
                                        </span>
                                        <span className="shrink-0 text-xs text-text-tertiary tabular-nums">
                                            {normalizeDialCode(parsed.dialCode)}
                                        </span>
                                    </button>
                                );
                            })
                        )}
                    </div>
                </PopoverContent>
            </Popover>

            {/* Grouped for reading, raw E.164 kept in state. `dir="ltr"` because a
                phone number is never mirrored, even in an RTL locale. */}
            <input
                dir="ltr"
                inputMode="tel"
                aria-label="Phone number"
                placeholder="Enter a number"
                value={formatPhoneAsYouType(value)}
                onChange={(e) => onChange(cleanPhoneValue(e.target.value))}
                className="min-w-0 flex-1 bg-transparent text-center text-[length:var(--voice-number-size,1.25rem)] leading-tight font-medium tracking-[0.02em] text-text-primary tabular-nums placeholder:text-[length:var(--voice-number-placeholder-size,0.875rem)] placeholder:font-normal placeholder:tracking-normal placeholder:text-text-tertiary focus-visible:outline-none"
            />

            <button
                type="button"
                aria-label="Delete last digit"
                title="Delete (hold to clear)"
                disabled={value === ''}
                onClick={handleBackspace}
                onPointerDown={startHold}
                onPointerUp={cancelHold}
                onPointerCancel={cancelHold}
                onPointerLeave={cancelHold}
                className="flex shrink-0 items-center justify-center rounded-xl p-2 text-text-secondary transition-colors hover:bg-white/70 hover:text-text-primary focus-visible:ring-2 focus-visible:ring-primary-500/50 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-30 dark:hover:bg-white/[0.12]"
            >
                <Delete className="size-4" aria-hidden="true" />
            </button>
        </div>
    );
}
